import React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { parseBody } from './releaseNotesUtils';
import { colors } from '../../colors';

// Type for a single release entry
export interface Release {
  version?: string;
  name?: string;
  tag_name?: string;
  date?: string;
  published_at?: string;
  body?: string | null;
  summary?: string;
}

// Helper function to get category icon and color
type CategoryStyle = { icon: string; color: string; bgColor: string };
export const getCategoryStyles = (category: string): CategoryStyle => {
  const categoryMap: Record<string, CategoryStyle> = {
    Features: {
      icon: '✨',
      color: colors.primaryBrightBlue,
      bgColor: colors.primaryBrightBlue + '20',
    },
    Improvements: { icon: '🚀', color: colors.yellowBright, bgColor: colors.yellowBright + '20' },
    'Bug Fixes': {
      icon: '🐞',
      color: colors.secondaryRoyalBlue,
      bgColor: colors.secondaryRoyalBlue + '20',
    },
    'Breaking Changes': {
      icon: '⚠️',
      color: colors.errorRedLight,
      bgColor: colors.errorRedLight + '20',
    },
    Documentation: { icon: '📚', color: colors.blueGrayMid, bgColor: colors.blueGrayMid + '20' },
    Refactor: { icon: '🔧', color: colors.grayMid, bgColor: colors.grayMid + '20' },
    Security: { icon: '🔒', color: colors.primaryDeepBlue, bgColor: colors.primaryDeepBlue + '20' },
    Performance: { icon: '⚡', color: colors.yellowMustard, bgColor: colors.yellowMustard + '20' },
  };
  return (
    categoryMap[category] || { icon: '📝', color: colors.grayMid, bgColor: colors.grayMid + '20' }
  );
};

// Styled components
const EntryContainer = styled.div`
  padding: 4px 0;
`;

const VersionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  color: ${colors.primaryBrightBlue};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-family: ${props => props.theme.fontFamilyMono};
  margin-bottom: 12px;
`;

const SummaryBlockquote = styled.blockquote`
  margin: 0 0 20px 0;
  padding: 12px 16px;
  border-left: 4px solid ${colors.primaryBrightBlue};
  background: ${props => props.theme.panelBackground}80;
  border-radius: 4px;
  font-size: 15px;
  line-height: 1.5;
  color: ${props => props.theme.textColor};
  font-style: italic;
  opacity: 0.95;
`;

const ReleaseDate = styled.div`
  color: ${props => props.theme.subtextColor};
  font-size: 13px;
  margin-bottom: 16px;
  opacity: 0.7;
`;

const CategorySection = styled.div`
  margin-top: 18px;
  &:first-child {
    margin-top: 0;
  }
`;

const CategoryHeader = styled.div<{ $color: string; $bgColor: string }>`
  display: flex;
  align-items: center;
  background: ${props => props.$bgColor};
  border: 1px solid ${props => props.$color}30;
  border-radius: 6px;
  padding: 10px 16px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$color};
  letter-spacing: -0.01em;
  transition: all 0.15s cubic-bezier(0.1, 0.9, 0.2, 1);

  &:hover {
    background: ${props => props.$bgColor}CC;
    border-color: ${props => props.$color}50;
    transform: translateY(-1px);
  }
`;

const CategoryIcon = styled.span`
  font-size: 16px;
  margin-right: 10px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
`;

const CategoryTitle = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 1em;
`;

const ChangeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    position: relative;
    padding: 10px 0 10px 28px;
    margin-bottom: 6px;
    color: ${props => props.theme.textColor};
    line-height: 1.5;
    font-size: 14px;
    border-radius: 4px;
    transition: all 0.15s cubic-bezier(0.1, 0.9, 0.2, 1);

    &::before {
      content: '•';
      position: absolute;
      left: 12px;
      color: ${colors.primaryBrightBlue};
      font-weight: bold;
      font-size: 16px;
      top: 10px;
    }

    &:hover,
    &:focus {
      background: ${props => props.theme.panelBackgroundHover}80;
      color: ${props => props.theme.textColorHl};
      transform: translateX(4px);
      outline: none;

      &::before {
        color: ${colors.primaryBrightBlue};
        transform: scale(1.2);
      }
    }
  }
`;

/**
 * Props for the ReleaseEntry component.
 */
interface ReleaseEntryProps {
  /** Release data to display */
  release: Release;
  /** Styled Components theme */
  theme: DefaultTheme;
  /**
   * When true, renders in compact mode (version + summary only, no categories or date).
   * Used in the modal to show multiple releases in a scannable list format.
   * @default false
   */
  compact?: boolean;
}

/**
 * Displays a single release entry with version, summary, and optional categorized changes.
 * Supports both full detail mode (with categories) and compact mode (summary only).
 */
const ReleaseEntry: React.FC<ReleaseEntryProps> = ({ release, theme, compact = false }) => {
  const body = typeof release.body === 'string' ? release.body : '';
  const parsedCategories = compact ? {} : parseBody(body);
  const version = release.version || release.name || release.tag_name || 'Unknown';
  const publishedDate = release.date || release.published_at;

  return (
    <EntryContainer>
      <VersionBadge>v{version.replace(/^v/, '')}</VersionBadge>

      {release.summary && <SummaryBlockquote theme={theme}>{release.summary}</SummaryBlockquote>}

      {!compact && publishedDate && (
        <ReleaseDate theme={theme}>{new Date(publishedDate).toLocaleDateString()}</ReleaseDate>
      )}

      {!compact &&
        Object.entries(parsedCategories).map(([category, items]) => {
          const categoryStyles = getCategoryStyles(category);
          const categoryKey = `${category.replace(/\s+/g, '-').toLowerCase()}`;
          return (
            <CategorySection key={categoryKey}>
              <CategoryHeader
                $color={categoryStyles.color}
                $bgColor={categoryStyles.bgColor}
                aria-label={`Category: ${category}`}
              >
                <CategoryIcon>{categoryStyles.icon}</CategoryIcon>
                <CategoryTitle>{category}</CategoryTitle>
              </CategoryHeader>
              <ChangeList theme={theme} aria-label={`Changes in ${category}`}>
                {(items as string[]).map((item: string, itemIndex: number) => {
                  const itemKey = `${categoryKey}-item-${itemIndex}-${item.slice(0, 20).replace(/\W/g, '')}`;
                  return <li key={itemKey}>{item}</li>;
                })}
              </ChangeList>
            </CategorySection>
          );
        })}
    </EntryContainer>
  );
};

export default ReleaseEntry;
